<template>
	<div class="pills-container">
		<div v-if="genreGroup" class="genre-group">
			<div>{{ genreGroup }}</div>
			<span>></span>
		</div>

		<HiddenHorizontalScrollbar class="subgenre-filters" v-if="orderedPills && orderedPills.length > 0">
			<div v-for="{ name, shortName, moviesUrl, showsUrl } in orderedPills" :key="name">
				<NuxtLink
					v-if="(isMoviesFiltered && moviesUrl) || (!isMoviesFiltered && showsUrl)"
					event
					:to="isMoviesFiltered ? moviesUrl?.fullPath : showsUrl?.fullPath"
					:class="{ 'router-link-active': isActive(shortName ?? '') }"
					@click.native.prevent="subgenreClicked(shortName ?? '')"
				>
					{{ name }}
				</NuxtLink>
				<div
					v-else
					class="filter-pill"
					@click="subgenreClicked(shortName ?? '')"
					:class="{ 'router-link-active': isActive(shortName ?? '') }"
				>
					{{ name }}
				</div>
			</div>
		</HiddenHorizontalScrollbar>
	</div>
</template>

<script setup lang="ts">
import type { GetOrderedSubgenresQuery } from '@/pages/graphql/queries/GetSubgenres.query';

import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';

import { CollectionType } from '@/enums/collection-type';
import { useFilterStore } from '@/stores/pinia/useFilterStore';
import { TitleObjectType } from '@/interfaces/titles';

const props = defineProps<{
	orderedSubgenres: GetOrderedSubgenresQuery['genres'];
	subgenresList: GetOrderedSubgenresQuery['subgenres'];
}>();

const router = useRouter();

const filterStore = useFilterStore();

const activeContentType = computed(() => {
	return filterStore.filters(CollectionType.POPULAR)['content_types'] as string[];
});

const isMoviesFiltered = computed(() => {
	return activeContentType.value.includes(TitleObjectType.MOVIE);
});

const isShowsFiltered = computed(() => {
	return activeContentType.value.includes(TitleObjectType.SHOW);
});

const activeSubgenreFilter = computed(() => {
	return filterStore.filters(CollectionType.POPULAR).subgenres as string[];
});

const activeGenresFilter = computed(() => {
	return filterStore.filters(CollectionType.POPULAR).genres as string[];
});

const isSingleGenreWithSubgenres = computed(() => {
	//only show genreGroup > pills when genre has subgenres. Currently 3 or 4 genres have no subs.
	return (
		activeGenresFilter.value.length > 0 &&
		props.orderedSubgenres.find(
			group =>
				group.shortName === activeGenresFilter.value[0] &&
				group.subgenreMovies.length > 0 &&
				group.subgenreShows.length > 0
		)
	);
});

// show genre breadcrumb-style text before pills, if subgenre filter active, else if one genre filter active
const genreGroup = computed(() => {
	if (
		activeContentType.value.length !== 1 ||
		(activeSubgenreFilter.value.length === 0 && activeGenresFilter.value.length !== 1)
	)
		return '';

	if (activeSubgenreFilter.value.length === 1) {
		return (
			props.orderedSubgenres.find(group =>
				group.subgenreMovies.find(subgenre => subgenre.content.shortName === activeSubgenreFilter.value[0])
			)?.translation ??
			props.subgenresList.find(subgenre => subgenre.content.shortName === activeSubgenreFilter.value[0])?.content
				.name
		);
	} else if (isSingleGenreWithSubgenres.value) {
		return props.orderedSubgenres.find(group => group.shortName === activeGenresFilter.value[0])?.translation;
	}
	return '';
});

const orderedSubgenreByShow = computed(() => {
	return (
		props.orderedSubgenres
			.find(group =>
				group.subgenreShows.find(subgenre => subgenre.content.shortName === activeSubgenreFilter.value[0])
			)
			?.subgenreShows.map(sub => sub.content) ??
		props.subgenresList
			.filter(subgenre => subgenre.content.shortName === activeSubgenreFilter.value[0])
			.map(sub => sub.content)
	);
});

const orderedSubgenreByMovie = computed(() => {
	return (
		props.orderedSubgenres
			.find(group =>
				group.subgenreShows.find(subgenre => subgenre.content.shortName === activeSubgenreFilter.value[0])
			)
			?.subgenreMovies.map(sub => sub.content) ??
		props.subgenresList
			.filter(subgenre => subgenre.content.shortName === activeSubgenreFilter.value[0])
			.map(sub => sub.content)
	);
});
const orderedSubgenresByShowAndGenre = computed(() => {
	return props.orderedSubgenres
		.find(group => group.shortName === activeGenresFilter.value[0])
		?.subgenreShows.map(sub => sub.content);
});

const orderedSubgenresByMovieAndGenre = computed(() => {
	return props.orderedSubgenres
		.find(group => group.shortName === activeGenresFilter.value[0])
		?.subgenreMovies.map(sub => sub.content);
});

const orderedPillsMoviesNoFilters = computed(() => {
	return props.subgenresList
		.map(sub => sub.content)
		.filter(sub => sub.moviesUrl && sub.moviesUrl.fullPath !== null)
		.slice(0, 21);
});

const orderedPillsShowsNoFilters = computed(() => {
	return props.subgenresList
		.map(sub => sub.content)
		.filter(sub => sub.showsUrl && sub.showsUrl.fullPath !== null)
		.slice(0, 21);
});

const orderedPills = computed(() => {
	if (
		activeContentType.value.length === 0 ||
		(activeSubgenreFilter.value.length === 0 && activeGenresFilter.value.length > 1)
	)
		return [];

	// if single subgenre, show  pills by subgenre (related subs) and content. else if genre, show pills by genre and content.
	// if no filters, or multiple filters, show order by SV. hardcoded values to be replaced by BE.
	if (isShowsFiltered.value) {
		if (activeSubgenreFilter.value.length > 0) return orderedSubgenreByShow.value;
		else if (activeGenresFilter.value.length === 1) {
			return isSingleGenreWithSubgenres.value ? orderedSubgenresByShowAndGenre.value : '';
		}

		return orderedPillsShowsNoFilters.value;
	}

	if (isMoviesFiltered.value) {
		if (activeSubgenreFilter.value.length > 0) {
			return orderedSubgenreByMovie.value;
		} else if (activeGenresFilter.value.length === 1) {
			return isSingleGenreWithSubgenres.value ? orderedSubgenresByMovieAndGenre.value : '';
		} else {
			return orderedPillsMoviesNoFilters.value;
		}
	}
	return [];
});

function isActive(subgenreShortName: string) {
	return ((filterStore.filters(CollectionType.POPULAR).subgenres as string[]) ?? []).some(
		subgenre => subgenre === subgenreShortName
	);
}

async function subgenreClicked(subgenreShortName: string) {
	const newRoute = await filterStore.toggle({
		filterName: 'subgenres',
		value: subgenreShortName,
		selectedState: isActive(subgenreShortName) ? 0 : 1,
	});

	if (newRoute) router.push(newRoute);
}
</script>

<style scoped lang="scss">
@import '~@/styles/sports.scss';
@import '~@/styles/variables.scss';

.pills-container {
	display: flex;
	align-items: center;
	gap: 10px;
	width: 100%;
	.genre-group {
		color: $jw-color-lightgray--dark;
		font-weight: 500;
		font-size: 14px;
		white-space: nowrap;
		user-select: none;
		display: flex;
		gap: 8px;
		align-items: center;
	}
	@media (max-width: 1050px) {
		margin-inline-start: 15px;
	}
}

.subgenre-filters {
	display: flex;
	gap: 8px;
	align-items: center;

	padding: 2px 6px;
	padding-inline-start: 8px;
	border-radius: 14px;
	background-color: $color-primary-dark;

	@media (max-width: 1050px) {
		margin-right: 30px;
	}
	@media (max-width: 767px) {
		margin-left: 0px;
	}

	.beta {
		display: inline-block;
		font-size: 12px;
		border-radius: 8px;
		margin-inline-end: 8px;
		color: $color-brand-default;
	}

	a,
	.filter-pill {
		display: inline-block;
		padding: 4px 8px;
		border-radius: 8px;
		background-color: $color-primary-dark;

		color: white;
		font-size: 14px;
		line-height: 16px;
		letter-spacing: 0.24px;
		white-space: nowrap;
		cursor: pointer;

		transition: background-color 0.1s linear, padding 0.1s linear;

		&.router-link-active {
			padding-inline: 16px;
			background-color: $color-neutral-neutral-dark;
			border-radius: 14px;
		}

		&.router-link-inactive {
			padding-inline: 16px;
			background-color: $color-primary-dark;
		}

		margin-inline-end: 8px;
		&:only-child {
			@media screen and (max-width: 600px) {
				margin-inline-end: 0px !important;
			}
		}
		&:last-child {
			margin: 0;
			@media screen and (max-width: 600px) {
				margin-inline-end: 16px;
			}
		}
	}

	:deep(.hidden-horizontal-scrollbar__items) {
		padding: 0;
	}
}
</style>
